import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { EnvVar, EnvVarsEditor } from './EnvVarsEditor';
import { ResourcesEditor, ResourcesValue } from './ResourcesEditor';

export interface PerEnvFieldsEntry {
  serviceType: string;
  serviceUri: string;
  image: string;
  serviceAccountIamRoleArn: string;
  certificateArn?: string;
  jwksUri?: string;
  envVars?: EnvVar[];
  availabilityZones?: string[];
  resources?: ResourcesValue;
}

export type PerEnvFieldsValue = Record<string, PerEnvFieldsEntry>;

const emptyEntry = (): PerEnvFieldsEntry => ({
  serviceType: 'internal',
  serviceUri: '',
  image: '',
  serviceAccountIamRoleArn: '',
  envVars: [],
});

const internalSubdomain = (env: string): string => {
  const certEnv = env === 'sandbox-resources' ? 'sandbox' : env;
  return `${certEnv}.rescale.internal`;
};

/**
 * Custom scaffolder field that renders a vertical card per selected environment
 * with inputs for: Service Type (public/internal), Service URI, Container Image
 * URI, ServiceAccount IAM Role ARN, ACM Certificate ARN (public only), availability zones,
 * and JWKS URI (optional).
 *
 * Reads `environments` from sibling form data. Emits a map keyed by env name.
 *
 * Usage: `ui:field: PerEnvFields` on a field of type `object`.
 */
export const PerEnvFieldsField = ({
  onChange,
  rawErrors,
  formContext,
  formData,
  schema,
}: FieldExtensionComponentProps<PerEnvFieldsValue>) => {
  const formValues = formContext?.formData as Record<string, any> | undefined;
  const environments: string[] = formValues?.environments ?? [];
  const currentValue: PerEnvFieldsValue = formData ?? {};

  // Raw text state per env for the Availability Zones input so users can type
  // commas / spaces freely without normalization stripping trailing chars.
  const [azText, setAzText] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const [env, entry] of Object.entries(currentValue)) {
      if (entry.availabilityZones?.length) {
        init[env] = entry.availabilityZones.join(', ');
      }
    }
    return init;
  });

  // Prune entries (and azText buffers) for removed envs whenever environments changes.
  useEffect(() => {
    const envSet = new Set(environments);
    const hasExtra = Object.keys(currentValue).some(k => !envSet.has(k));
    const hasMissing = environments.some(e => !(e in currentValue));
    if (hasExtra || hasMissing) {
      const pruned: PerEnvFieldsValue = {};
      for (const env of environments) {
        pruned[env] = currentValue[env] ?? emptyEntry();
      }
      onChange(pruned);
    }
    setAzText(prev => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (envSet.has(k)) next[k] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environments.join(',')]);

  const updateField = <K extends keyof PerEnvFieldsEntry>(
    env: string,
    field: K,
    value: PerEnvFieldsEntry[K],
  ) => {
    const existing = currentValue[env] ?? emptyEntry();
    const isEmpty = value === '' || (Array.isArray(value) && value.length === 0);
    // Optional fields are omitted entirely when empty (rather than stored as '' or []).
    if (isEmpty && (field === 'jwksUri' || field === 'certificateArn' || field === 'availabilityZones')) {
      const next: PerEnvFieldsEntry = { ...existing };
      delete next[field];
      onChange({ ...currentValue, [env]: next });
    } else {
      onChange({ ...currentValue, [env]: { ...existing, [field]: value } });
    }
  };

  const updateResources = (env: string, next: ResourcesValue) => {
    const existing = currentValue[env] ?? emptyEntry();
    const nextEntry: PerEnvFieldsEntry = { ...existing };
    if (Object.keys(next).length === 0) {
      delete nextEntry.resources;
    } else {
      nextEntry.resources = next;
    }
    onChange({ ...currentValue, [env]: nextEntry });
  };

  const handleAvailabilityZonesChange = (env: string, raw: string) => {
    setAzText(prev => ({ ...prev, [env]: raw }));
    const zones = raw.split(',').map(z => z.trim()).filter(Boolean);
    // Skip onChange when the parsed array is unchanged (e.g. typing a trailing
    // space/comma). Prevents form-context re-renders on every keystroke.
    const existing = currentValue[env]?.availabilityZones ?? [];
    if (zones.length === existing.length && zones.every((z, i) => z === existing[i])) {
      return;
    }
    updateField(env, 'availabilityZones', zones);
  };

  const errorMessage = rawErrors?.[0];
  const domainMessage = 'Domain for this service in the given environment';

  if (environments.length === 0) {
    return null;
  }

  return (
    <FormControl
      component="fieldset"
      margin="normal"
      fullWidth
      error={rawErrors?.length > 0 && !rawErrors[0]?.startsWith('__no__error__')}
    >
      <FormLabel component="legend">
        {schema.title || 'Per-Environment Fields'}
      </FormLabel>
      <FormHelperText>
        {schema.description ||
          'Configure service type, URIs, and credentials for each environment.'}
      </FormHelperText>
      {environments.map(env => {
        const entry = currentValue[env] ?? emptyEntry();
        const isPublic = (entry.serviceType ?? 'internal') === 'public';
        return (
          <Paper key={env} variant="outlined" style={{ padding: 16, marginTop: 16 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              {env}
            </Typography>
            <TextField
              select
              label="Service Type"
              required
              value={entry.serviceType ?? 'internal'}
              onChange={e => updateField(env, 'serviceType', e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              helperText="Public services are public-facing and require an ALB Ingress. Internal services have no external Ingress."
            >
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="public">Public</MenuItem>
            </TextField>
            {isPublic && (
              <TextField
                label="ACM Certificate ARN"
                required
                value={entry.certificateArn ?? ''}
                onChange={e => updateField(env, 'certificateArn', e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                helperText="ACM certificate ARN for the ALB, e.g. arn:aws:acm:us-west-2:123456789:certificate/abc-123"
              />
            )}
            <TextField
              label="Service URI"
              required
              value={entry.serviceUri ?? ''}
              onChange={e => updateField(env, 'serviceUri', e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              error={!isPublic && !!entry.serviceUri && !entry.serviceUri.endsWith(`.${internalSubdomain(env)}`)}
              helperText={
                isPublic
                  ? domainMessage
                  : `${domainMessage}. Must end with .${internalSubdomain(env)}`
              }
            />
            <TextField
              label="Container Image URI"
              required
              value={entry.image ?? ''}
              onChange={e => updateField(env, 'image', e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              helperText="Container image URI e.g. 855314458327.dkr.ecr.us-west-2.amazonaws.com/testecr:latest"
            />
            <TextField
              label="ServiceAccount IAM Role ARN"
              required
              value={entry.serviceAccountIamRoleArn ?? ''}
              onChange={e => updateField(env, 'serviceAccountIamRoleArn', e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              helperText="IRSA role ARN for the Deployment's ServiceAccount. e.g. arn:aws:iam::855314458327:role/testecr-irsa"
            />
            <TextField
              label="Availability Zones (optional)"
              value={azText[env] ?? (entry.availabilityZones ?? []).join(', ')}
              onChange={e => handleAvailabilityZonesChange(env, e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              helperText="Comma-separated list of AWS availability zones (AZs) to restrict pod scheduling to (e.g. us-east-1a, us-east-1b). Leave empty to allow any zone. Should match the AZs the ALB is deployed in — otherwise pods may land in an AZ the ALB can't route to."
            />
            <TextField
              label="JWKS URI (optional)"
              value={entry.jwksUri ?? ''}
              onChange={e => updateField(env, 'jwksUri', e.target.value)}
              fullWidth
              margin="dense"
              variant="outlined"
              helperText="URI for JSON Web Key Set, used for JWT authentication. Required if the service exposes a public endpoint and uses JWTs for auth."
            />
            <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 4 }}>
              Container Resources (optional)
            </Typography>
            <FormHelperText style={{ marginBottom: 8 }}>
              Per-env overrides for CPU/memory requests and limits. Each field is independently optional — leave blank to inherit from the base Deployment defaults.
            </FormHelperText>
            <ResourcesEditor
              value={entry.resources ?? {}}
              onChange={next => updateResources(env, next)}
            />
            <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 4 }}>
              Environment Variables
            </Typography>
            <EnvVarsEditor
              value={entry.envVars ?? []}
              onChange={next => updateField(env, 'envVars', next)}
            />
          </Paper>
        );
      })}
      {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};
